import { TicketIssueType } from '../tickets';

export interface DeviceData {
  os: string;
  deviceType: string;
  language: string;
  manufacturer: string;
  model: string;
  osVersion: string;
  region: string;
  sdkVersion: string;
}

export interface RegisterDeviceInput {
  identifier: string;
  firebaseToken: string;
  data: DeviceData;
}

export type NotificationType = ResidentNotificationActionType | ResidentEmailNotificationActionType;

export enum ResidentNotificationActionType {
  CONTRACT_END_NEW_ADDRESS = 'CONTRACT_END_NEW_ADDRESS',
  DOCUMENT_SHARED = 'DOCUMENT_SHARED',
  LEGAL_TEXT = 'LEGAL_TEXT',
  NEW_CONTENT = 'NEW_CONTENT',
  NEW_CONTENT_NEWS = 'NEW_CONTENT_NEWS',
  NEW_CONTENT_OFFER = 'NEW_CONTENT_OFFER',
  PAYMENT_SHARED = 'PAYMENT_SHARED',
  TICKET_MESSAGE = 'TICKET_MESSAGE',
  TICKET_STATUS_CHANGE = 'TICKET_STATUS_CHANGE',
}

export enum ResidentEmailNotificationActionType {
  NEW_NEWS = 'NEW_NEWS', // contentId
  NEW_ANNOUNCEMENT = 'NEW_ANNOUNCEMENT', // contentId
  NEW_OFFER = 'NEW_OFFER', // contentId
  NEW_DIGITAL_DOCUMENTS_CONSENT = 'NEW_DIGITAL_DOCUMENTS_CONSENT', // contentId
  NEW_LEGAL_TEXT = 'NEW_LEGAL_TEXT', // contentId

  NEW_DOCUMENT = 'NEW_DOCUMENT', // documentId, documentType, documentName

  NEW_TICKET_MESSAGE = 'NEW_TICKET_MESSAGE', // ticketId, issueType
  NEW_TICKET_STATUS = 'NEW_TICKET_STATUS', // ticketId, issueType

  NEW_ADDRESS = 'NEW_ADDRESS', // contractId, contractExternalId, propertyAddress

  // maybe different types for MASTERDATA_CHANGE and VERIFY_EMAIL
  MASTERDATA_CHANGE_REQUEST_TIMED_OUT = 'MASTERDATA_CHANGE_REQUEST_TIMED_OUT', // masterdataChangeRequestType
  VERIFY_EMAIL = 'VERIFY_EMAIL',

  // Improvement: add new payment shared notification
}

interface INotificationBase {
  notificationType: NotificationType;
}

export interface IContentNotification extends INotificationBase {
  contentId: string;
}

export interface ITicketNotification extends INotificationBase {
  ticketId: string;
  issueType: TicketIssueType;
}

export interface IDocumentNotification extends INotificationBase {
  documentId: string;
}

export interface IContractNotification extends INotificationBase {
  contractId: string;
  contractExternalId: string;
  propertyAddress: string;
}

// actually like RemoteMessage from @nativescript/firebase-messaging but ony with data property
// because it's the only one we need and to put a nativescript relevant thing into a shared web and
// mobile base service
export interface PushMessage {
  data?: undefined | { [key: string]: string };
}

export type NotificationData =
  | IContentNotification
  | ITicketNotification
  | IDocumentNotification
  | IContractNotification;

export function isIContentNotification(data: NotificationData): data is IContentNotification {
  return (
    data.notificationType === ResidentEmailNotificationActionType.NEW_NEWS ||
    data.notificationType === ResidentNotificationActionType.NEW_CONTENT_NEWS ||
    data.notificationType === ResidentEmailNotificationActionType.NEW_ANNOUNCEMENT ||
    data.notificationType === ResidentNotificationActionType.NEW_CONTENT ||
    data.notificationType === ResidentEmailNotificationActionType.NEW_OFFER ||
    data.notificationType === ResidentNotificationActionType.NEW_CONTENT_OFFER ||
    data.notificationType === ResidentEmailNotificationActionType.NEW_DIGITAL_DOCUMENTS_CONSENT ||
    data.notificationType === ResidentEmailNotificationActionType.NEW_LEGAL_TEXT ||
    data.notificationType === ResidentNotificationActionType.LEGAL_TEXT
  );
}

export function isITicketNotification(data: NotificationData): data is ITicketNotification {
  return (
    data.notificationType === ResidentEmailNotificationActionType.NEW_TICKET_MESSAGE ||
    data.notificationType === ResidentNotificationActionType.TICKET_MESSAGE ||
    data.notificationType === ResidentEmailNotificationActionType.NEW_TICKET_STATUS ||
    data.notificationType === ResidentNotificationActionType.TICKET_STATUS_CHANGE
  );
}

export function isIDocumentNotification(data: NotificationData): data is IDocumentNotification {
  return (
    data.notificationType === ResidentEmailNotificationActionType.NEW_DOCUMENT ||
    data.notificationType === ResidentNotificationActionType.DOCUMENT_SHARED
  );
}

export function isIContractNotification(data: NotificationData): data is IContractNotification {
  return (
    data.notificationType === ResidentEmailNotificationActionType.NEW_ADDRESS ||
    data.notificationType === ResidentNotificationActionType.CONTRACT_END_NEW_ADDRESS
  );
}

export function isPushMessage(value: string[] | PushMessage): value is PushMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    typeof (value as PushMessage).data === 'object'
  );
}
